// In-game scoreboard (Tab): connected players, latency, full config, host kick/transfer.

import { $, escapeHtml } from "../dom.js";
import { buildConfigDetailsHtml } from "./labels.js";
import { pingBadge } from "./lobby.js";
import { net, renderer, state, TEAM_TINT } from "../state.js";

export function renderScoreboard(): void {
  if (!state.currentLobby) return;
  const lobby = state.currentLobby;
  const ctf = lobby.config.mode === "ctf";
  const sm = lobby.config.ctfScoreMode;
  const conquest = ctf && (sm === "conquest" || sm === "carry");
  const teams = lobby.config.mode === "teams" || ctf;
  const lms = lobby.config.mode === "lms"; // rank by survival (lives), not points
  const isHost = lobby.hostId === state.playerId;
  const snap = renderer.latest();
  // Metric column: conquest points / flags captured (CTF), lives (LMS), else score.
  const metricById = new Map<string, number>();
  if (snap) {
    for (const t of snap.tanks) {
      metricById.set(t.id, conquest ? Math.round(t.score) : ctf ? t.captures : lms ? t.livesLeft : t.score);
    }
  }

  const rows = [...lobby.players].sort(
    (a, b) => (metricById.get(b.id) ?? 0) - (metricById.get(a.id) ?? 0)
  );
  const metricLabel = conquest ? "Points" : ctf ? "Flags" : lms ? "Lives" : "Score";
  const maxMetric = Math.max(1, ...rows.map((p) => metricById.get(p.id) ?? 0));
  const body = rows
    .map((p, i) => {
      const metric = metricById.get(p.id) ?? 0;
      // Score bar relative to the leader; a metric of 0 shows an empty track.
      const pct = metric > 0 ? Math.max(4, Math.round((metric / maxMetric) * 100)) : 0;
      const teamChip = teams
        ? `<span class="sbr-team" style="color:${lobby.teamColors[p.team] ?? TEAM_TINT[p.team % TEAM_TINT.length]}">${escapeHtml(lobby.teamNames[p.team] ?? `Team ${p.team + 1}`)}</span>`
        : "";
      const actions =
        isHost && p.id !== state.playerId
          ? `<span class="sbr-act">` +
            `<button class="sb-give ghost small" data-id="${p.id}"${p.connected ? "" : " disabled"} title="Transfer host to this player">Give host</button>` +
            `<button class="sb-kick ghost small" data-id="${p.id}">Kick</button>` +
            `</span>`
          : "";
      const tag = p.id === state.playerId ? '<span class="sb-you">you</span>' : "";
      const host = p.id === lobby.hostId ? '<span class="sb-host">host</span>' : "";
      return (
        `<li class="sbr${p.id === state.playerId ? " me" : ""}${p.connected ? "" : " sb-off"}">` +
        `<span class="sbr-rank${i < 3 ? ` r${i + 1}` : ""}">${i + 1}</span>` +
        `<span class="swatch" style="background:${p.color}"></span>` +
        `<span class="sbr-main">` +
        `<span class="sbr-name">${escapeHtml(p.name)}${tag}${host}${teamChip}</span>` +
        `<span class="sbr-bar"><i style="width:${pct}%"></i></span>` +
        `</span>` +
        `<span class="sbr-score">${metric}</span>` +
        `<span class="sbr-ping">${pingBadge(p.id)}</span>` +
        actions +
        `</li>`
      );
    })
    .join("");

  $("sb-table-wrap").innerHTML =
    `<p class="sbr-sub">${rows.length} player${rows.length === 1 ? "" : "s"} · ranked by ${metricLabel.toLowerCase()}</p>` +
    `<ol class="sb-rows">${body}</ol>`;

  $("sb-table-wrap")
    .querySelectorAll<HTMLButtonElement>(".sb-kick")
    .forEach((b) => {
      b.onclick = () => net.send({ type: "kickPlayer", targetId: b.dataset.id ?? "" });
    });

  $("sb-table-wrap")
    .querySelectorAll<HTMLButtonElement>(".sb-give")
    .forEach((b) => {
      b.onclick = () => net.send({ type: "transferHost", targetId: b.dataset.id ?? "" });
    });

  $("sb-details").innerHTML = buildConfigDetailsHtml(lobby);
}

export function openScoreboard(): void {
  if (!state.inGame) return;
  state.scoreboardOpen = true;
  renderScoreboard();
  $("scoreboard").classList.remove("hidden");
  if (state.scoreboardTimer) clearInterval(state.scoreboardTimer);
  state.scoreboardTimer = setInterval(renderScoreboard, 500); // live scores + latency
}

export function closeScoreboard(): void {
  state.scoreboardOpen = false;
  $("scoreboard").classList.add("hidden");
  if (state.scoreboardTimer) {
    clearInterval(state.scoreboardTimer);
    state.scoreboardTimer = null;
  }
}
